import { useNavigate } from "@tanstack/react-router";
import {
  supportKinds,
  supportPriorities,
  supportPriorityWords,
  supportTicketLabel,
  supportUrgencies,
  supportUrgencyWords,
  type AdminSupportCard,
  type AdminSupportStaff,
  type AdminSupportStatus,
} from "@teamna/shared";
import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  DataTable,
  ErrorState,
  PageHeader,
  Select,
  Toolbar,
  type ChipOption,
  type DataTableColumn,
} from "@teamna/ui/components/index.ts";
import { formatCountdown, formatDateTime, formatRelativeDay } from "@teamna/web-client/lib/format.ts";
import { useStaffMe, useSupportBoard, useSupportList } from "../lib/cc-api.ts";
import {
  AssigneeBadge,
  ColorDot,
  KIND_WORDS,
  NO_FILTERS,
  PRIORITY_TONES,
  SupportTabs,
  TagChips,
  TeamFilterSelects,
  filtering,
  liveFilters,
  matchesFilters,
  staffName,
  useStoredFilters,
} from "./support-parts.tsx";

/**
 * The support list, `/support/list` (support-system.md, section 6.2): every ticket in one table,
 * resolved ones of any age too, newest activity first.
 *
 * The search runs on the server, because it reads every message, notes too. The filters (kind,
 * column, and the filters the board has: team member, label, priority, urgency, demo clubs) run in
 * the browser on the rows the search returned, like the Businesses table: the volume is small and a
 * filter that waits for the API is slow.
 */

const ALL = "all";
const SEARCH_DELAY_MS = 300;

function columnsFor(statuses: Map<string, AdminSupportStatus>, staff: AdminSupportStaff[]): DataTableColumn<AdminSupportCard>[] {
  return [
    {
      id: "ticket",
      header: "Ticket",
      sortValue: (row) => row.number,
      cell: (row) => (
        <div className="min-w-[220px]">
          <p className="text-[12px] text-ink-2 tabular-nums">{supportTicketLabel(row.number)}</p>
          <p dir="auto" className="font-semibold text-ink">
            {row.subject}
          </p>
          <p className="text-[12px] text-ink-2">
            <span dir="auto">{row.business.name}</span>
            {row.business.demo ? " · Demo" : ""}
          </p>
        </div>
      ),
    },
    {
      id: "column",
      header: "Column",
      sortValue: (row) => statuses.get(row.statusId)?.position ?? null,
      cell: (row) => {
        const status = statuses.get(row.statusId);
        return status ? (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <ColorDot color={status.color} />
            {status.name}
          </span>
        ) : (
          KIND_WORDS[row.kind]
        );
      },
    },
    {
      id: "labels",
      header: "Labels",
      sortValue: (row) => row.tags.map((tag) => tag.name).join(", ") || null,
      cell: (row) =>
        row.tags.length > 0 ? (
          <div className="max-w-[180px]">
            <TagChips tags={row.tags} />
          </div>
        ) : (
          <span className="text-ink-3">None</span>
        ),
    },
    {
      id: "assignee",
      header: "Team member",
      sortValue: (row) => (row.assigneeStaffEmail ? staffName(staff, row.assigneeStaffEmail) : null),
      hideOnPhone: true,
      cell: (row) =>
        row.assigneeStaffEmail ? (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <AssigneeBadge name={staffName(staff, row.assigneeStaffEmail)} />
            <span dir="auto">{staffName(staff, row.assigneeStaffEmail)}</span>
          </span>
        ) : (
          <span className="text-ink-3">Nobody</span>
        ),
    },
    {
      id: "priority",
      header: "Priority",
      sortValue: (row) => supportPriorities.indexOf(row.priority),
      hideOnPhone: true,
      cell: (row) =>
        row.priority === "none" ? <span className="text-ink-3">None</span> : <Badge tone={PRIORITY_TONES[row.priority]}>{supportPriorityWords[row.priority]}</Badge>,
    },
    {
      id: "urgency",
      header: "Urgency",
      sortValue: (row) => supportUrgencies.indexOf(row.urgency),
      hideOnPhone: true,
      cell: (row) => (
        <div className="flex flex-col items-start gap-1">
          <Badge tone={row.urgency === "blocking" ? "cancelled" : "quiet"}>{supportUrgencyWords[row.urgency]}</Badge>
          {row.waitingOnTeam && row.kind !== "closed" ? <Badge tone="late">Customer wrote</Badge> : null}
        </div>
      ),
    },
    {
      id: "activity",
      header: "Last activity",
      sortValue: (row) => Date.parse(row.lastActivityAt),
      cell: (row) => (
        <span className="whitespace-nowrap" title={formatDateTime(row.lastActivityAt)}>
          {formatCountdown(row.lastActivityAt)}
        </span>
      ),
    },
    {
      id: "created",
      header: "Opened",
      sortValue: (row) => Date.parse(row.createdAt),
      hideOnPhone: true,
      cell: (row) => (
        <span className="whitespace-nowrap" title={formatDateTime(row.createdAt)}>
          {formatRelativeDay(row.createdAt)}
        </span>
      ),
    },
  ];
}

export function SupportListScreen() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState<string>(ALL);
  const [column, setColumn] = useState<string>(ALL);
  const [storedFilters, setFilters] = useStoredFilters("list");
  const me = useStaffMe();
  const myEmail = me.data?.email ?? null;

  // The server search waits until the typing stops.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(query.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const list = useSupportList(search);
  const board = useSupportBoard();
  const statuses = useMemo(() => board.data?.statuses ?? [], [board.data]);
  const tags = useMemo(() => board.data?.tags ?? [], [board.data]);
  const staff = useMemo(() => board.data?.staff ?? [], [board.data]);
  const filters = useMemo(() => liveFilters(storedFilters, tags, staff), [storedFilters, tags, staff]);
  // A column archived since the filter was picked filters nothing (internal review F1.3).
  const columnFilter = column === ALL || statuses.some((status) => status.id === column) ? column : ALL;
  const statusById = useMemo(() => new Map(statuses.map((status) => [status.id, status])), [statuses]);
  const rows = useMemo(() => list.data?.tickets ?? [], [list.data]);

  // Every filter but the kind, so the kind chips count what the other filters leave.
  const filtered = useMemo(
    () =>
      rows.filter((row) => (columnFilter === ALL || row.statusId === columnFilter) && matchesFilters(row, filters, myEmail)),
    [rows, columnFilter, filters, myEmail],
  );
  const visible = useMemo(() => (kind === ALL ? filtered : filtered.filter((row) => row.kind === kind)), [filtered, kind]);

  const kindOptions = useMemo<ChipOption[]>(
    () => [
      { id: ALL, label: "All", count: filtered.length },
      ...supportKinds.map((id) => ({ id, label: KIND_WORDS[id], count: filtered.filter((row) => row.kind === id).length })),
    ],
    [filtered],
  );

  const tableColumns = useMemo(() => columnsFor(statusById, staff), [statusById, staff]);
  const narrowed = columnFilter !== ALL || kind !== ALL || filtering(filters);

  return (
    <>
      <PageHeader title="Support" description="Every ticket, resolved ones too. Search reads the number, the subject, every message and note, and the business name." />
      <SupportTabs current="list" />
      {list.error && !list.data ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : (
        <>
          <Toolbar
            search={{ value: query, onChange: setQuery, placeholder: "Search tickets, messages or businesses" }}
            filters={{ options: kindOptions, selectedId: kind, onSelect: setKind, label: "Filter by kind" }}
            actions={
              narrowed ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setKind(ALL);
                    setColumn(ALL);
                    setFilters(NO_FILTERS);
                  }}
                >
                  Clear the filters
                </Button>
              ) : null
            }
          >
            <Select label="Filter by column" value={columnFilter} onChange={(event) => setColumn(event.target.value)} className="sm:max-w-[200px]">
              <option value={ALL}>All columns</option>
              {statuses.map((status) => (
                <option key={status.id} value={status.id}>
                  {status.name}
                </option>
              ))}
            </Select>
            <TeamFilterSelects filters={filters} onChange={setFilters} tags={tags} staff={staff} />
          </Toolbar>
          {list.data?.truncated ? (
            <p className="mb-3 text-[13px] text-ink-2">Only the 500 tickets with the newest activity show. Search to find older ones.</p>
          ) : null}
          <DataTable
            columns={tableColumns}
            rows={visible}
            rowKey={(row) => row.id}
            rowLabel={(row) => `${supportTicketLabel(row.number)} ${row.subject}`}
            onRowClick={(row) => void navigate({ to: "/support/$ticketId", params: { ticketId: row.id } })}
            isLoading={list.isLoading}
            defaultSort={{ id: "activity", direction: "desc" }}
            countLabel={(count) => (count === 1 ? "1 ticket" : `${count} tickets`)}
            empty={
              search || narrowed
                ? { title: "No tickets match", message: "Change the search or the filters." }
                : { title: "No tickets yet", message: "A ticket shows here when an owner, an admin or a staff member asks for help." }
            }
          />
        </>
      )}
    </>
  );
}
